import {
    DEFAULT_DEV_LOGGING_SETTINGS,
    DEFAULT_PRODUCTION_LOGGING_SETTINGS,
    DOUBLE_PERCENTAGE_SUBSTITUTION,
    EMPTY_OBJECT,
    FLOAT_SUBSTITUTION,
    INTEGER_SUBSTITUTIONS,
    MAX_DEEP_COPY_LEVEL,
    MESSAGE_SPLIT_REGEX,
    STRING_SUBSTITUTION,
} from "./constants";
import { ILoggingSettings, LoggingMessage, LoggingMessageOrGenerator, PendingGroup, SubstituteMessageType } from "./types";

/**
 * Nahradí substituční znaky dle popisu v {@link ILogger.log}.
 *
 * Tato funkce slouží:
 * - Primárně pro unifikaci. Aby každá implementace {@link ILogger}-u toto mohla využít a my měli tak zajištěno, že to bude vždy stejné.
 * - Proto je použito i pro {@link ConsoleLogger}. Aby programátor věděl, že to co se mu zobrazuje do consoli
 * bude stejně předáváno i do jakýchkoli jiných potenciálních implementací {@link ILogger}.
 *
 * @param message Zpráva potenciálně obsahující substituční znaky.
 * @param args Argumenty odpovídající substitučním znakům. Budou použity pro substituci.
 * @returns Nahrazenou message + zbývající argumenty nepoužité pro nahrazení.
 */
export const substituteArguments = (message: LoggingMessage, ...args: any[]): SubstituteMessageType => (
    (typeof message === "string")
        ? substituteArgumentsForStringMessage(message, ...args)
        : {
            substitutedMessage: message,
            unsubstitutedArguments: args,
        }
);

/**
 * Vlastní implementace pro {@link substituteArguments}, pokud je `message` string.
 * @see substituteArguments
 */
const substituteArgumentsForStringMessage = (message: string, ...args: any[]): SubstituteMessageType => {
    const snippets = message.split(MESSAGE_SPLIT_REGEX);

    let isSubstitionTurn = true;
    let currentArgumentIndex = 0;

    const substitutedMessage = snippets.reduce((acc: string, snippet: string) => {
        isSubstitionTurn = !isSubstitionTurn;

        const isSnippetPossiblyReplacable = (isSubstitionTurn && (currentArgumentIndex < args.length));

        const { replacement, wasReplacementUsed } = isSnippetPossiblyReplacable
            ? trySubstitute(snippet, args[currentArgumentIndex])
            : { replacement: snippet, wasReplacementUsed: false };

        if (wasReplacementUsed) {
            ++currentArgumentIndex;
        }

        return acc + replacement;
    }, "");

    return {
        substitutedMessage,
        unsubstitutedArguments: args.slice(currentArgumentIndex),
    };
};

/**
 * Pokud zadaný `placeholder` představuje podporovaný substituční znak, pak vrátí patřičně modifikovaný `replacement`.
 * Jinak `placeholder`.
 */
const trySubstitute = (placeholder: string, replacement: any): { replacement: string | number; wasReplacementUsed: boolean } => {
    if (STRING_SUBSTITUTION === placeholder) {
        return {
            replacement: String(replacement),
            wasReplacementUsed: true,
        };
    } else if (INTEGER_SUBSTITUTIONS.indexOf(placeholder) >= 0) {
        return {
            replacement: parseInt(replacement),
            wasReplacementUsed: true,
        };
    } else if (FLOAT_SUBSTITUTION === placeholder) {
        return {
            replacement: parseFloat(replacement),
            wasReplacementUsed: true,
        };
    } else if (DOUBLE_PERCENTAGE_SUBSTITUTION === placeholder) {
        return {
            replacement: "%",
            wasReplacementUsed: false,
        };
    } else {
        return {
            replacement: placeholder,
            wasReplacementUsed: false,
        };
    }
};

/**
 * Určeno pro kopírování argumentů předaných do {@link ConsoleLogger}.
 * - Abychom zajistili, že se v console zobrazí data z doby zalogování. (Nikoli z doby zobrazení.)
 * - A aby tak programátor věděl, že to co se mu teď loguje do console, tak se mu stejně v budoucnu bude logovat i do jiných loggerů.
 *
 * Pozn. Při překročení maxDeepLevel vyhodí výjimku. Důsledky:
 * - výhoda: Výsledek `deepCopy` je vždy konzistentní. Tj. nikdy se nestane, že tam, kde původní `data` očekávají nonNull bychom vrátili null.
 * Nebo že místo čísla býchom vrátili string "truncated".
 * - nevýhoda: Úplne vstupní data (s ohledem na rekurzi) mohou být array (a v `ConsoleLogger-u` to tak je.)
 * Tím, že jedna jediná položka z tohoto array překročí `maxDeepLevel`, tak vyjíímka probublá až nahoru a přijdeme i o všechny ostatní položky z array.
 * - předpoklad a závěr: K překročení `maxDeeplevel` bude docházet jen zřídka. Proto je pro nás výhodnější zachovat typovou konzistenci a oželet případnou ztrátu.
 *
 * @param data Data ke zkopírování
 * @param maxDeepLevel Pojistka proti nekonečné rekurzi.
 * @throws Pokud je `maxDeepLevel <= 0`, pak vyhodí výjimku.
 */
export const deepCopy = (data: any, maxDeepLevel: number): any => {
    if (maxDeepLevel <= 0) {
        throw new Error("truncated, maxDeepLevel reached");
    }
    if (data === null || data === undefined) {
        return data;
    }

    // Error musí být ošetřen explicitně, protože obsahuje i metodu "toString", kterou ale obecný Object.getOwnPropertyNames níže nekopíruje.
    // "toString" je důležitá kvůli ConsoleLoggeru, aby se chyba logovala stejně, jako před zavedením Logging modulu.
    // Tj. Aby byl přímo pěkně vidět stack trace.
    if (data instanceof Error) {
        // Error by neměl odkazovat žádné reference, které by v čas zalogování odkazovaly na jiná data než v čas zobrazení.
        // Proto je možné vrátit Error napřímo - bez duplikace.
        return data;
    }

    switch (typeof data) {
        case "string":
        case "number":
        case "boolean":
        case "symbol":
            return data;
        case "object":
            if (Array.isArray(data)) {
                return (data.map((item: any) => deepCopy(item, maxDeepLevel - 1)));
            } else if (data as any instanceof Set) {
                return new Set([...data.values()].map((item: any) => deepCopy(item, maxDeepLevel - 1)));
            } else if (data as any instanceof Map) {
                const copiedEntries = [...data.entries()].map(([key, value]) => [key, deepCopy(value, maxDeepLevel - 1)]) as [any, any][];
                return new Map(copiedEntries);
            } else {
                // Sem se dostaneme jak pro POJO, tak pro instance tříd (třeba složitých tříd).
                // Je otázka, jak se k tomuto stavět.
                // - deepCopy je určena pro argumenty logování. A V ILogger je jasně zmíněno, že by se měly předávat pouze argumenty řádně serializovatelné.
                // - přičemž není stanoveno, co "serializovatelné" vlastně znamená
                // - POJO je OK
                // - co třeba new Error("some error")?
                // -    .. např. zde již není možné použít Object.entries(), ale Object.getOwnPropertyNames()
                // - a co další třídní instance (různých potomků s různým prototype chain a kdo ví jakými vnitřními privátními fieldy)
                // ROZHODL jsem se to kopírovat přes Object.getOwnPropertyNames().
                //    - doufám, že to bude pro většinu použití dostačující
                //    - a pokud se časem narazí na něco, co je žádoucí logovat, ale nevyhovuje to, tak se to pořeší pak
                // -- Jan Kalendovský 23. červen 2020
                // NEKOPÍRUJE to věci z prototype.
                //    - Např. metodu "Error.toString". (Proto je Error ošetřen výše zvlášť.)
                //    - Pokud bude žádoucí, aby do loggeru chodily i metody, musí se to předělat.
                //        - Je jak by metody logoval custom logger - např. logující přes internet nebo do databáze.
                return Object.getOwnPropertyNames(data)
                    .map((key) => [key, deepCopy(data[key], maxDeepLevel - 1)])
                    .reduce((acc, [key, value]) => {
                        return { ...acc, [key]: value };
                    }, EMPTY_OBJECT);
            }
        case "function":
            return data;
        default:
            throw new Error(`Unexpected data type: ${typeof data}`);
    }
};

/**
 * Obálka nad {@link deepCopy} kopírující pole a nastavující `maxDeepLevel` na {@link MAX_DEEP_COPY_LEVEL}.
 *
 * Nikdy nevyhodí výjimku. Místo výjimky raději vrátí prázdné pole.
 *
 * @param data Pole ke zkopírování
 */
export const deepArrayCopyOrEmptyIfError = (data: any[]): any[] => {
    try {
        return deepCopy(data, MAX_DEEP_COPY_LEVEL);
    } catch (e) {
        // K výjimce může dojít např. při překročení MAX_DEEP_COPY_LEVEL.
        // Počítá se s tím, že by toto mělo být zřídkavé.
        // Pak je výhodnější mít konzistentní implementaci deepCopy (viz deepCopy) a přijít i o všechny další argumenty,
        // které MAX_DEEP_COPY_LEVEL nepřekročily.
        return [];
    }
};

/**
 * @param isDevelopomentMode True, pokud MW běží v development režimu. Pro tento režim může funkce vrátit jiné nastavení, než pro produkci.
 * @return Výchozí nastavení logování.
 */
export const getDefaultLoggingSettings = (isDevelopomentMode: boolean): ILoggingSettings => {
    return isDevelopomentMode ? DEFAULT_DEV_LOGGING_SETTINGS : DEFAULT_PRODUCTION_LOGGING_SETTINGS;
};

/**
 * Vytvoří a vrátí novou instanci typu {@link PendingGroup} dle zadaných parametrů.
 * @param messageOrGenerator
 * @param args
 * @param isCollapsed
 */
export const createPendingGroup = (messageOrGenerator: LoggingMessageOrGenerator, args: any[], isCollapsed: boolean): PendingGroup => ({
    messageOrGenerator,
    args,
    isCollapsed,
});
